import { Graph } from '@antv/x6';

/**
 * 注册过程性边类型：
 * - Consumption：过程消耗对象
 * - Effect：过程影响对象
 * - Generation：过程生成对象
 * - Invocation：过程调用对象
 * - Agent Enablement：Agent 使能关系（实心圆点箭头 + ¬C 标识）
 *
 * 所有类型均使用平滑贝塞尔曲线并搭配自定义箭头标记。
 */
export function registerProceduralEdges() {
  // 影响箭头标记（所有过程性类型共用）
  const influenceArrow = {
    name: 'influence-arrow',
    args: {
      size: { width: 16, height: 16 },
      dx: -2,
      dy: -8,
      d: 'M 0 0 L 12 8 L 0 16 L 4 8 Z', // 空心影响箭头
      fill: '#ffffff',
      stroke: '#595959',
      strokeWidth: 1.5,
    },
  };

  // Consumption：对象 → 过程（箭头在目标端）
  Graph.registerEdge(
    'opm-procedural-consumption',
    {
      inherit: 'edge',
      attrs: {
        line: {
          stroke: '#595959',
          strokeWidth: 1.8,
          targetMarker: {
            name: 'path',
            args: {
              d: 'M 12 0 L 0 8 L 12 16 L 8 8 Z',
              fill: '#ffffff',
              stroke: '#595959',
              strokeWidth: 1.5,
            },
          },
        },
      },
      router: {
        name: 'orth',
        args: {
          padding: 20,
        },
      },
      connector: { name: 'jumpover', args: { size: 5 } },
    },
    true,
  );

  // Effect：对象 → 过程（目标端为实心填充箭头）
  Graph.registerEdge(
    'opm-procedural-effect',
    {
      inherit: 'edge',
      attrs: {
        line: {
          stroke: '#595959',
          strokeWidth: 2,
          targetMarker: {
            name: 'path',
            args: {
              d: 'M 14 0 L 0 10 L 14 20 L 6 10 Z',
              fill: '#595959',
              stroke: '#595959',
              strokeWidth: 1.5,
            },
          },
        },
      },
      router: {
        name: 'orth',
        args: {
          padding: 20,
        },
      },
      connector: { name: 'jumpover', args: { size: 5 } },
    },
    true,
  );

  // Generation：过程 → 对象（箭头在目标端）
  Graph.registerEdge(
    'opm-procedural-generation',
    {
      inherit: 'edge',
      attrs: {
        line: {
          stroke: '#595959',
          strokeWidth: 1.8,
          targetMarker: {
            name: 'path',
            args: {
              d: 'M 12 0 L 0 8 L 12 16 L 8 8 Z',
              fill: '#ffffff',
              stroke: '#595959',
              strokeWidth: 1.5,
            },
          },
        },
      },
      router: {
        name: 'orth',
        args: {
          padding: 20,
        },
      },
      connector: { name: 'jumpover', args: { size: 5 } },
    },
    true,
  );

  // Invocation：过程 → 过程（箭头在目标端）
  Graph.registerEdge(
    'opm-procedural-invocation',
    {
      inherit: 'edge',
      attrs: {
        line: {
          stroke: '#595959',
          strokeWidth: 1.8,
          targetMarker: {
            name: 'path',
            args: {
              d: 'M 12 0 L 0 8 L 12 16 L 8 8 Z',
              fill: '#ffffff',
              stroke: '#595959',
              strokeWidth: 1.5,
            },
          },
        },
      },
      router: {
        name: 'orth',
        args: {
          padding: 20,
        },
      },
      connector: { name: 'jumpover', args: { size: 5 } },
    },
    true,
  );

  // Agent Enablement：Agent 使能关系（实心圆点箭头 + ¬C 标识）
  Graph.registerEdge(
    'opm-procedural-agent-enablement',
    {
      inherit: 'edge',
      attrs: {
        line: {
          stroke: '#595959',
          strokeWidth: 2,
          targetMarker: {
            name: 'circle',
            args: {
              r: 6,
              fill: '#595959',
              stroke: '#595959',
              strokeWidth: 1.5,
            },
          },
        },
        label: {
          text: '¬C',
          fontSize: 10,
          fill: '#595959',
          fontWeight: 'bold',
          textAnchor: 'middle',
          textVerticalAnchor: 'bottom',
          distance: 0.95,
          offset: { x: 0, y: -8 },
        },
      },
      router: {
        name: 'orth',
        args: {
          padding: 20,
        },
      },
      connector: { name: 'jumpover', args: { size: 5 } },
    },
    true,
  );
}

/**
 * 辅助函数：根据过程性子类型获取对应的边形状名称
 */
export function getProceduralEdgeShape(
  subtype?: string,
): string {
  switch (subtype) {
    case 'consumption':
      return 'opm-procedural-consumption';
    case 'effect':
      return 'opm-procedural-effect';
    case 'generation':
      return 'opm-procedural-generation';
    case 'invocation':
      return 'opm-procedural-invocation';
    case 'agent-enablement':
      return 'opm-procedural-agent-enablement';
    default:
      return 'opm-procedural-consumption';
  }
}
